/* ==========================================================================
   shell.js — the furniture's behaviour. Two things, and nothing else.
   No ScrollTrigger, no GSAP: the shell must be correct before any animation
   library has parsed and must survive one failing to load.

   1. THE SPINE. Three custom properties, all written from here:

      --spine-dx   the sub-pixel remainder between the grid anchor's x and the
                   nearest whole DEVICE pixel. The axis takes its x from the
                   12-column grid, whose margin and gutter are clamp()s, so at
                   1440 column 2 starts at 182.64px and the browser splits the
                   1px stroke across device columns 182 and 183 — measured at
                   rgb(217,182,154) where the CSS says rgb(225,150,113). The
                   rule, the footer figurine and the footer's closing hairline
                   all read this same number. Recomputed on resize and
                   fonts.ready, never per scroll frame — the x cannot change
                   while scrolling.

      --spine-p    scroll depth. main.css turns it into a `height`, NOT a
                   transform: scaleY, because scaleY would scale the 140px
                   terminus with everything else and collapse the fade to a
                   crisp cut.

      --spine-stop the distance from the top of the screen to the footer's 2px
                   cut line, clamped into the viewport, sizing both the grey
                   rule and the ink. The axis terminates ON that cut line rather
                   than running past it down the footer's left edge.

      --spine-fade is capped at the ink's remaining travel (stop x (1 − p)) so
      the dissolve cannot outrun the ink and evaporate short of the junction.

      The catch-up is duration-limited, not an asymptotic lerp, so it lands
      exactly on target in 300ms and a screenshot after a jump shows the true
      depth rather than 98% of it.

   2. THE STICKY BUY BAR, at every width. It hangs off the HERO's bottom edge:
      the first moment at which the visitor has both read the price and scrolled
      past it. It used to hang off the assembly, which was second on the page
      when that was decided and is fifth now — the bar would have been absent
      for five of eight screens on a page whose whole failure was that commerce
      arrived late. `Adventoria.buyBar.revealAfter(el)` still lets any section
      claim it; `?buybar` forces it on so the component is inspectable in a
      still.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduced = root.classList.contains('reduced-motion');
  var search = '';
  try { search = window.location.search || ''; } catch (e) {}

  window.Adventoria = window.Adventoria || {};

  /* ------------------------------------------------------------------
     1. THE SPINE
     ------------------------------------------------------------------ */
  var SCRUB_MS = 300;
  var TERMINUS = 140; /* px of dissolve at the ink's end — matches main.css's default */
  var fill = document.querySelector('[data-spine-fill]');
  var spine = document.querySelector('.spine');
  var anchor = document.querySelector('[data-spine-anchor]');
  var footer = document.querySelector('.site-footer');
  var current = 0, from = 0, to = 0, t0 = 0, raf = null;
  var stopPx = -1;

  /* The anchor is zero-width and carries no dx of its own, so its rect.left is
     the grid's true un-corrected x and the correction cannot feed back into
     itself. dpr is re-read every time: dragging a window between a retina and a
     non-retina display changes it without changing the layout. */
  var dxPx = null;
  function snap() {
    if (!anchor) return;
    var x = anchor.getBoundingClientRect().left;
    var dpr = window.devicePixelRatio || 1;
    var dx = Math.round(x * dpr) / dpr - x;
    /* So a 0.0001px jitter in a rect never rewrites the property and never
       invalidates the page's paint. */
    if (dxPx !== null && Math.abs(dx - dxPx) < 0.0005) return;
    dxPx = dx;
    root.style.setProperty('--spine-dx', dx.toFixed(4) + 'px');
  }

  function progress() {
    var max = (document.documentElement.scrollHeight || 0) - window.innerHeight;
    if (max <= 0) return 0;
    var p = (window.pageYOffset || document.documentElement.scrollTop || 0) / max;
    return p < 0 ? 0 : (p > 1 ? 1 : p);
  }

  /* One rect read per frame, clamped into the viewport. Must stay BEFORE the
     writes in paint() and never interleave with them, or the frame costs two
     forced layouts instead of one. */
  function measureStop() {
    var h = window.innerHeight || 0;
    var s = h;
    if (footer) {
      var t = footer.getBoundingClientRect().top;
      s = t < 0 ? 0 : (t > h ? h : t);
    }
    stopPx = Math.round(s);
  }

  function paint(p) {
    if (!fill) return;
    if (spine) {
      spine.style.setProperty('--spine-stop', stopPx + 'px');
      /* Capped at the ink's remaining travel so it cannot outrun the ink. */
      var fade = stopPx * (1 - p);
      spine.style.setProperty('--spine-fade', (fade < TERMINUS ? Math.round(fade) : TERMINUS) + 'px');
    }
    /* Four decimals is one sub-pixel on a 4000px-tall document. */
    fill.style.setProperty('--spine-p', p.toFixed(4));
  }

  function tick(now) {
    measureStop();
    var k = (now - t0) / SCRUB_MS;
    if (k >= 1) {
      current = to;
      paint(current);
      raf = null;
      return;
    }
    /* easeOutQuad — the catch-up decelerates into its target the way a scrub does. */
    var e = 1 - (1 - k) * (1 - k);
    current = from + (to - from) * e;
    paint(current);
    raf = window.requestAnimationFrame(tick);
  }

  function onResize() {
    snap();
    onScroll();
  }

  function onScroll() {
    measureStop();
    var next = progress();
    if (reduced || !window.requestAnimationFrame) {
      to = current = next;
      paint(current);
      return;
    }
    if (Math.abs(next - to) < 0.0002) {
      /* Depth has not moved, but the footer may have (resize, a section growing
         after its font landed), so re-register the terminus anyway. */
      if (!raf) paint(current);
      return;
    }
    to = next;
    from = current;
    t0 = window.performance ? window.performance.now() : Date.now();
    /* No will-change: this element animates neither transform nor opacity, so a
       hint would buy a compositor layer for nothing. */
    if (!raf) raf = window.requestAnimationFrame(tick);
  }

  if (fill) {
    snap();
    measureStop();
    paint(0);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('load', onResize);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(onResize).catch(function () {});
    }
    onScroll();
  }

  /* ------------------------------------------------------------------
     2. THE STICKY BUY BAR
     ------------------------------------------------------------------ */
  var bar = document.querySelector('[data-buy-bar]');
  var watching = null;

  function setBar(on) {
    root.classList.toggle('buy-bar-in', !!on);
  }

  function watchTick() {
    if (!watching) return;
    var box = watching.getBoundingClientRect();
    setBar(box.bottom <= 0);
  }

  var buyBar = {
    get element() { return bar; },
    reveal: function () { watching = null; setBar(true); },
    hide: function () { watching = null; setBar(false); },
    /* The bar arrives once `target`'s bottom edge has left the top of the
       viewport, and leaves again if the visitor scrolls back above it. */
    revealAfter: function (target) {
      var el = typeof target === 'string' ? document.querySelector(target) : target;
      if (!el) return false;
      watching = el;
      watchTick();
      return true;
    }
  };

  if (bar) {
    window.addEventListener('scroll', watchTick, { passive: true });
    window.addEventListener('resize', watchTick, { passive: true });

    /* THE BAR NAMES ITS OWN ANCHOR, and falls back to the hero.

       The homepage's bar hangs off the hero — the first moment the visitor has
       both read the price and scrolled past it. The product page renders its
       OWN bar (it has to: it submits a real form, carries the variant's price
       and must say "sold out" when the variant is), and that bar hangs off the
       inline add-to-cart instead. One attribute keeps the rule in the markup
       next to the component it governs rather than as a selector list in here.

       A template with neither (collection, cart, search) finds nothing,
       `watching` stays null and the bar simply never arrives — correct, since
       those pages carry their own buy affordances. */
    buyBar.revealAfter(bar.getAttribute('data-reveal-after') || '[data-hero-box]');

    if (/[?&]buybar\b/.test(search)) buyBar.reveal();
  }

  window.Adventoria.buyBar = buyBar;
  window.Adventoria.spine = { progress: function () { return current; } };
})();
